/**
 * ConnectionManager — ONE WebPubSub connection, kept alive, shared by every chat.
 *
 * Frames from different chats interleave freely; which chat a frame belongs to is
 * answered by the FRAME (see [frameOwnership](../stream/frameOwnership.ts)), not
 * by the socket it arrived on. This file owns only what is per-connection: the
 * URL provider (token refresh across `authSignIn`), suspend / half-open detection
 * the SDK cannot see, replacing a client the SDK has given up on, and the status
 * the composer's banner reads.
 */

import logger from 'src/utils/logger';

import type { ChatId, ConnectionStatus, Unsubscribe } from './ChatTransport';
import type { EncryptionCodec } from './EncryptionCodec';
import type {
  ConnectionEventListener,
  ManagedConnection,
  ManagedConnectionState,
  WsUrlProvider
} from './ManagedConnection';
import { createManagedConnection } from './ManagedConnection';

export interface ConnectionManagerConfig {
  idlePingMs: number;
  reconnectDelayMs: number;
  maxReconnectDelayMs: number;
  reconnectGraceMs: number;
}

export const DEFAULT_CONNECTION_CONFIG: ConnectionManagerConfig = {
  idlePingMs: 30_000,
  reconnectDelayMs: 250,
  maxReconnectDelayMs: 30_000,
  reconnectGraceMs: 5_000
};

const SUSPEND_GAP_FACTOR = 4;

const SDK_RETRY_HINT_MS = 1_000;

export interface ConnectionManagerOptions {
  getUrl: WsUrlProvider;
  codec: EncryptionCodec;
  config?: Partial<ConnectionManagerConfig>;
  factory?: (
    getUrl: WsUrlProvider,
    codec: EncryptionCodec
  ) => ManagedConnection;
}

export interface ConnectionManager {
  start(): Promise<void>;
  stop(): Promise<void>;
  ensureConnection(): Promise<string>;
  getConnectionId(): string | undefined;
  subscribe(chatId: ChatId, listener: ConnectionEventListener): Unsubscribe;
  markTurnStarted(chatId: ChatId): void;
  markTurnEnded(chatId: ChatId): void;
  onStatusChange(listener: (status: ConnectionStatus) => void): Unsubscribe;
  getStatus(): ConnectionStatus;
  getSubscriptionCount(): number;
}

interface Subscription {
  chatId: ChatId;
  listener: ConnectionEventListener;
  off?: Unsubscribe;
}

export function createConnectionManager(
  opts: ConnectionManagerOptions
): ConnectionManager {
  const config: ConnectionManagerConfig = {
    ...DEFAULT_CONNECTION_CONFIG,
    ...(opts.config ?? {})
  };
  const factory =
    opts.factory ??
    ((getUrl: WsUrlProvider, codec: EncryptionCodec) =>
      createManagedConnection({ getUrl, codec }));

  const subscriptions = new Set<Subscription>();
  const statusListeners = new Set<(status: ConnectionStatus) => void>();
  const turnsInFlight = new Set<ChatId>();

  let connection: ManagedConnection | undefined;
  let status: ConnectionStatus = { kind: 'idle' };
  let started = false;
  let connecting: Promise<string> | undefined;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectDelayMs = config.reconnectDelayMs;
  let attempt = 0;
  let outages = 0;

  const setStatus = (next: ConnectionStatus) => {
    const same =
      next.kind === status.kind &&
      (next.kind !== 'open' ||
        status.kind !== 'open' ||
        next.connectionId === status.connectionId) &&
      (next.kind !== 'retrying' ||
        status.kind !== 'retrying' ||
        next.attempt === status.attempt);
    if (same) {
      return;
    }
    status = next;
    statusListeners.forEach((l) => {
      try {
        l(next);
      } catch (error) {
        /* One bad subscriber must not stop the others, but it must not vanish
         * either — this fires at most twice per turn, so logging is cheap. */
        logger.error('[chat:transport] status listener threw', { error });
      }
    });
  };

  const attachAll = (conn: ManagedConnection) => {
    subscriptions.forEach((sub) => {
      sub.off = conn.subscribe(sub.chatId, sub.listener);
    });
  };

  const detachAll = () => {
    subscriptions.forEach((sub) => {
      sub.off?.();
      sub.off = undefined;
    });
  };

  const teardown = () => {
    detachAll();
    const dying = connection;
    connection = undefined;
    void dying?.stop();
  };

  function scheduleReconnect(delayMs: number): void {
    if (reconnectTimer || !started) {
      return;
    }
    setStatus(
      attempt > 0
        ? { kind: 'retrying', attempt, nextAttemptAt: Date.now() + delayMs }
        : { kind: 'connecting' }
    );
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect().catch((error) => {
        /* `connect`'s own catch already re-armed the backoff; this only records
         * that the scheduled attempt failed. */
        logger.error('[chat:transport] scheduled reconnect failed', { error });
      });
    }, delayMs);
  }

  const recycle = (immediate: boolean) => {
    if (!started || connecting) {
      return;
    }
    teardown();
    scheduleReconnect(immediate ? 0 : reconnectDelayMs);
  };

  const publishConnectionState = (state: ManagedConnectionState) => {
    if (state === 'open') {
      outages = 0;
      setStatus({
        kind: 'open',
        connectionId: connection?.getConnectionId() ?? ''
      });
      return;
    }
    if (state === 'reconnecting') {
      outages += 1;
      setStatus({
        kind: 'retrying',
        attempt: outages,
        nextAttemptAt: Date.now() + SDK_RETRY_HINT_MS
      });
    }
  };

  async function connect(): Promise<string> {
    if (connecting) {
      return connecting;
    }
    connecting = (async () => {
      setStatus({ kind: 'connecting' });
      const conn = factory(opts.getUrl, opts.codec);
      connection = conn;
      attachAll(conn);
      conn.onStateChange((state) => {
        if (connection !== conn) {
          return;
        }
        if (connecting) {
          return;
        }
        if (state === 'degraded') {
          recycle(false);
          return;
        }
        publishConnectionState(state);
      });
      try {
        await conn.start();
      } catch (err) {
        attempt += 1;
        reconnectDelayMs = Math.min(
          reconnectDelayMs * 2,
          config.maxReconnectDelayMs
        );
        teardown();
        scheduleReconnect(reconnectDelayMs);
        throw err;
      }
      attempt = 0;
      outages = 0;
      reconnectDelayMs = config.reconnectDelayMs;
      const id = conn.getConnectionId() ?? '';
      setStatus({ kind: 'open', connectionId: id });
      return id;
    })().finally(() => {
      connecting = undefined;
    });
    return connecting;
  }

  const waitForOpen = (conn: ManagedConnection): Promise<string | undefined> =>
    new Promise((resolve) => {
      let done = false;
      const finish = (value: string | undefined) => {
        if (done) {
          return;
        }
        done = true;
        off();
        clearTimeout(timer);
        resolve(value);
      };
      const timer = setTimeout(
        () => finish(undefined),
        config.reconnectGraceMs
      );
      const off = conn.onStateChange((state) => {
        if (state === 'open') {
          finish(conn.getConnectionId() ?? undefined);
        } else if (state === 'degraded' || state === 'disposed') {
          finish(undefined);
        }
      });
    });

  let lastHeartbeatAt = Date.now();
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  const checkForSuspend = () => {
    const now = Date.now();
    const gap = now - lastHeartbeatAt;
    lastHeartbeatAt = now;
    if (gap <= config.idlePingMs * SUSPEND_GAP_FACTOR) {
      return;
    }
    if (turnsInFlight.size > 0) {
      return;
    }
    if (!connection || connection.getHealth() === 'healthy') {
      return;
    }
    recycle(true);
  };

  const startHeartbeat = () => {
    if (heartbeatTimer || !started) {
      return;
    }
    lastHeartbeatAt = Date.now();
    heartbeatTimer = setInterval(checkForSuspend, config.idlePingMs);
  };

  return {
    async start() {
      if (started) {
        return;
      }
      started = true;
      startHeartbeat();
      await connect().catch((error) => {
        /* Failure already re-armed the reconnect; boot must not reject. */
        logger.error('[chat:transport] initial connect failed', { error });
      });
    },

    async stop() {
      if (!started) {
        return;
      }
      started = false;
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      detachAll();
      const dying = connection;
      connection = undefined;
      subscriptions.clear();
      turnsInFlight.clear();
      await dying?.stop();
      setStatus({ kind: 'idle' });
    },

    async ensureConnection() {
      startHeartbeat();
      checkForSuspend();
      const conn = connection;
      if (conn) {
        const state = conn.getState();
        if (state === 'open' && conn.getHealth() !== 'degraded') {
          const id = conn.getConnectionId();
          if (id) {
            return id;
          }
        }
        if (state === 'reconnecting' || state === 'connecting') {
          const id = await waitForOpen(conn);
          if (id) {
            return id;
          }
        }
      }
      return connect();
    },

    getConnectionId() {
      return connection?.getConnectionId();
    },

    subscribe(chatId, listener) {
      const sub: Subscription = { chatId, listener };
      subscriptions.add(sub);
      sub.off = connection?.subscribe(chatId, listener);
      return () => {
        sub.off?.();
        sub.off = undefined;
        subscriptions.delete(sub);
      };
    },

    markTurnStarted(chatId) {
      turnsInFlight.add(chatId);
    },

    markTurnEnded(chatId) {
      turnsInFlight.delete(chatId);
    },

    onStatusChange(listener) {
      statusListeners.add(listener);
      try {
        listener(status);
      } catch (error) {
        logger.error('[chat:transport] status listener threw on subscribe', {
          error
        });
      }
      return () => statusListeners.delete(listener);
    },

    getStatus() {
      return status;
    },

    getSubscriptionCount() {
      return subscriptions.size;
    }
  };
}
